import { useCallback, type ReactNode } from 'react'
import { usePermissionCheck, useTeamCache, useTranslation, useUpdateUser } from '@front/model'
import { useToastError } from '@hooks'
import { useDisclosure } from '@mantine/hooks'
import { showNotification } from '@mantine/notifications'
import { type TeamData, type TinyUser, type UserData, type UserId } from '@terros/common'
import { TButton } from 'app/components/buttons'
import { PickUserDrawer } from 'app/components/pickers'
import { TListItem, UserListItem, type TListItemProps } from 'app/components/terros'

type Props = {
  user: UserData
  team: TeamData
}

export const ManagerInfo = ({ user, team }: Props): ReactNode => {
  const { t } = useTranslation()
  const { update, isUpdating, error } = useUpdateUser()
  const cache = useTeamCache()
  const [isOpen, { open, close }] = useDisclosure()
  useToastError(error)
  const { data: canChangeManager } = usePermissionCheck({ permissionType: 'user:manage', targetId: user?.userId })

  const handleManagerChange = useCallback(
    async (manager: TinyUser) => {
      await update({ userId: user?.userId, managerId: manager.userId })
      cache.clearList()
      cache.clearItem(team.teamId)
      showNotification({
        message: t('Manager successfully updated'),
        color: 'green',
      })
    },
    [update, user?.userId, t, cache, team]
  )

  return (
    <>
      <ReportsTo
        managerId={user?.managerId}
        rightSection={
          canChangeManager ? (
            <TButton loading={isUpdating} variant='secondary' onClick={open}>
              {t('Change')}
            </TButton>
          ) : undefined
        }
      />
      <PickUserDrawer isOpen={isOpen} close={close} onSelect={handleManagerChange} />
    </>
  )
}

const ReportsTo = ({
  managerId,
  rightSection,
}: { managerId: UserId | undefined } & Pick<TListItemProps, 'rightSection'>): ReactNode => {
  const { t } = useTranslation()

  if (managerId) {
    return (
      <UserListItem
        detailBold
        label={t('Reports to')}
        details={({ userName }) => userName}
        userId={managerId}
        rightSection={rightSection}
      />
    )
  }

  return <TListItem label={t('Reports to no one')} rightSection={rightSection} />
}
